import React, { useCallback, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Pressable, StyleSheet } from 'react-native';
import { View } from 'react-native';

import { BodyText, Icon, theme, usePressableState } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const AddMoreIngredientsButton = (props: Props) => {
  const { modificationGroupId, modificationGroupName, isDisabled, onPress } = props;

  const { formatMessage } = useIntl();

  // ─── Interactive ───────────────────────────────────────────────────────────

  const ref = useRef(null);
  const { isInteracting } = usePressableState(ref);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handleOnPress = useCallback(() => {
    onPress(modificationGroupId, modificationGroupName);
  }, [modificationGroupId, modificationGroupName, onPress]);

  // ───────────────────────────────────────────────────────────────────────────

  const formattedModificationGroupName = modificationGroupName.toLowerCase();
  const label = formattedModificationGroupName
    ? formatMessage(messages.labelDynamic, {
        modification_group_name: formattedModificationGroupName,
      }) // prettier-ignore
    : formatMessage(messages.labelStatic);

  return (
    <Pressable
      ref={ref}
      accessibilityRole="link"
      accessibilityLabel={label}
      style={styles.container}
      disabled={isDisabled}
      onPress={handleOnPress}
    >
      <View style={styles.icon}>
        <Icon
          width={16}
          height={16}
          name="IconPlus2"
          color={isDisabled ? theme.colors.OPACITY.DARK_KALE.DARK : undefined}
        />
      </View>

      <BodyText
        underline={Boolean(isInteracting && !isDisabled)}
        style={isDisabled ? styles.disabledLabel : styles.label}
        sizeMatch={['18']}
      >
        {label}
      </BodyText>
    </Pressable>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  labelStatic: {
    defaultMessage: 'Add more ingredients',
    description: 'Customization | Add more ingredients button | Static label',
  },
  labelDynamic: {
    defaultMessage: 'Add {modification_group_name}',
    description: 'Customization | Add more ingredients button | Dynamic label',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const ICON_SIZE = 30;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    paddingVertical: theme.spacing['1'],
    paddingHorizontal: theme.spacing['2'],
    gap: theme.spacing['2'],
  },
  icon: {
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
    width: ICON_SIZE,
    height: ICON_SIZE,
    borderRadius: ICON_SIZE,
    alignItems: 'center',
    justifyContent: 'center',
  },
  label: {
    color: theme.colors.KALE,
  },
  disabledLabel: {
    color: theme.colors.OPACITY.DARK_KALE.DARK,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  modificationGroupId: string;
  modificationGroupName: string;
  isDisabled: boolean;
  onPress: (modificationGroupId: string, modificationGroupName: string) => void;
};
